/**
 * Sync Ledger - Persists per-project sync state across sessions.
 *
 * SEQUENCE OWNERSHIP: Sequence numbers are owned here and persisted in
 * localStorage so they survive reloads. They are NEVER stored only in
 * runtime memory. Every call to nextSequence() immediately writes to storage.
 *
 * Storage key: nuvra-sync-ledger (single JSON object keyed by projectId)
 */

import { logger } from "@/system/logger";

const LEDGER_KEY = "nuvra-sync-ledger";

export type SyncStatus = "synced" | "pending" | "failed" | "offline" | "conflict";

export interface SyncLedgerEntry {
  projectId:            string;
  localSequenceNum:     number; // last sequence assigned locally
  cloudSequenceNum:     number; // last sequence confirmed in cloud
  lastSyncedAt:         number; // epoch ms of last successful cloud sync (0 if never)
  status:               SyncStatus;
  pendingCount:         number; // how many local sequences exceed cloud
  consecutiveFailures:  number;
  lastError:            string | null;
}

type LedgerStore = Record<string, SyncLedgerEntry>;

/* =========================================================
   INTERNAL HELPERS
   ========================================================= */

function readStore(): LedgerStore {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(LEDGER_KEY);
    return raw ? (JSON.parse(raw) as LedgerStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: LedgerStore): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LEDGER_KEY, JSON.stringify(store));
  } catch (err) {
    logger.error("SyncLedger write failed", { err });
  }
}

function defaultEntry(projectId: string): SyncLedgerEntry {
  return {
    projectId,
    localSequenceNum:    0,
    cloudSequenceNum:    0,
    lastSyncedAt:        0,
    status:              "synced",
    pendingCount:        0,
    consecutiveFailures: 0,
    lastError:           null,
  };
}

/* =========================================================
   SYNC LEDGER API
   ========================================================= */

export const SyncLedger = {

  get(projectId: string): SyncLedgerEntry {
    const store = readStore();
    if (!store[projectId]) {
      store[projectId] = defaultEntry(projectId);
      writeStore(store);
    }
    return { ...store[projectId] };
  },

  /**
   * Increment local sequence number and persist immediately.
   * Returns the NEW sequence number assigned to the pending save.
   */
  nextSequence(projectId: string): number {
    const store = readStore();
    if (!store[projectId]) store[projectId] = defaultEntry(projectId);
    const entry = store[projectId];
    entry.localSequenceNum += 1;
    entry.pendingCount = entry.localSequenceNum - entry.cloudSequenceNum;
    entry.status = "pending";
    store[projectId] = entry;
    writeStore(store);
    return entry.localSequenceNum;
  },

  markSynced(projectId: string, sequenceNum: number): void {
    const store = readStore();
    if (!store[projectId]) return;
    const entry = store[projectId];
    if (sequenceNum > entry.cloudSequenceNum) {
      entry.cloudSequenceNum    = sequenceNum;
      entry.lastSyncedAt        = Date.now();
    }
    entry.pendingCount        = Math.max(0, entry.localSequenceNum - entry.cloudSequenceNum);
    entry.status              = entry.pendingCount === 0 ? "synced" : "pending";
    entry.consecutiveFailures = 0;
    entry.lastError           = null;
    store[projectId] = entry;
    writeStore(store);
  },

  markFailed(projectId: string, error: string): void {
    const store = readStore();
    if (!store[projectId]) return;
    store[projectId].consecutiveFailures += 1;
    store[projectId].lastError            = error;
    store[projectId].status               = "failed";
    writeStore(store);
  },

  markOffline(projectId: string): void {
    const store = readStore();
    if (!store[projectId]) return;
    store[projectId].status = "offline";
    writeStore(store);
  },

  markConflict(projectId: string): void {
    const store = readStore();
    if (!store[projectId]) return;
    store[projectId].status = "conflict";
    writeStore(store);
  },

  resolveConflict(projectId: string): void {
    const store = readStore();
    if (!store[projectId]) return;
    const e = store[projectId];
    e.status = e.pendingCount === 0 ? "synced" : "pending";
    store[projectId] = e;
    writeStore(store);
  },

  getAll(): SyncLedgerEntry[] {
    return Object.values(readStore());
  },

  getPending(): SyncLedgerEntry[] {
    return Object.values(readStore()).filter(e => e.localSequenceNum > e.cloudSequenceNum);
  },
};
